/**
 * AdminDomain — Página para tenant configurar domínio personalizado
 * Fluxo: digitar domínio → instruções CNAME → verificar DNS
 */

import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { T } from '../styles/tokens';

const CNAME_TARGET = 'app.whitelabelclaw.com';

export function AdminDomain() {
  const [domain, setDomain] = useState('');
  const [savedDomain, setSavedDomain] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState<boolean | null>(null);
  const [verifyMessage, setVerifyMessage] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  // Carregar domínio atual
  useEffect(() => {
    api.get<any>('/tenant/domain').then((res: any) => {
      if (res?.domain) {
        setDomain(res.domain);
        setSavedDomain(res.domain);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Salvar domínio
  const handleSave = async () => {
    if (!domain.trim()) { setError('Digite um domínio'); return; }
    setSaving(true);
    setError('');
    setVerified(null);
    setVerifyMessage('');
    try {
      const res = await api.put<any>('/tenant/domain', { domain: domain.trim().toLowerCase() });
      setSavedDomain(res?.domain || domain.trim().toLowerCase());
    } catch (e: any) {
      setError(e?.message || 'Erro ao salvar domínio');
    } finally {
      setSaving(false);
    }
  };

  // Remover domínio
  const handleRemove = async () => {
    if (!confirm('Tem certeza que deseja remover o domínio personalizado?')) return;
    setSaving(true);
    try {
      await api.delete('/tenant/domain');
      setDomain('');
      setSavedDomain(null);
      setVerified(null);
      setVerifyMessage('');
    } catch (e: any) {
      setError(e?.message || 'Erro ao remover domínio');
    } finally {
      setSaving(false);
    }
  };

  // Verificar DNS
  const handleVerify = async () => {
    setVerifying(true);
    setVerified(null);
    setVerifyMessage('');
    try {
      const res = await api.get<any>('/tenant/domain/verify');
      setVerified(res?.verified ?? false);
      setVerifyMessage(res?.message || '');
    } catch (e: any) {
      setVerified(false);
      setVerifyMessage(e?.message || 'Erro ao verificar DNS');
    } finally {
      setVerifying(false);
    }
  };

  // Copiar CNAME
  const handleCopy = () => {
    navigator.clipboard.writeText(CNAME_TARGET).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', color: T.textMuted }}>
        Carregando...
      </div>
    );
  }

  const s = {
    page: { maxWidth: 640, margin: '0 auto', padding: '32px 24px' } as const,
    title: { fontSize: 24, fontWeight: 800, color: T.text, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 } as const,
    subtitle: { fontSize: 14, color: T.textMuted, marginBottom: 32 } as const,
    card: {
      background: 'rgba(255,255,255,0.03)', border: `1px solid ${T.border}`, borderRadius: 12, padding: 24, marginBottom: 24,
    } as const,
    label: { fontSize: 13, fontWeight: 600, color: T.textMuted, marginBottom: 8, display: 'block' } as const,
    inputRow: { display: 'flex', gap: 8, marginBottom: 16 } as const,
    input: {
      flex: 1, background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`, borderRadius: 8,
      padding: '10px 14px', color: T.text, fontSize: 14, outline: 'none',
    } as const,
    btn: {
      background: T.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px',
      fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' as const,
    } as const,
    btnOutline: {
      background: 'transparent', color: T.primary, border: `1px solid ${T.primary}`, borderRadius: 8,
      padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' as const,
    } as const,
    btnDanger: {
      background: 'transparent', color: '#EF4444', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8,
      padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    } as const,
    cnameCard: {
      background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)', borderRadius: 10,
      padding: 20, marginBottom: 20,
    } as const,
    cnameRow: {
      display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
    } as const,
    cnameLabel: { fontSize: 12, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase' as const, letterSpacing: 0.5 } as const,
    cnameValue: { fontSize: 14, fontWeight: 600, color: T.text, fontFamily: 'monospace' } as const,
    status: (ok: boolean | null) => ({
      display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderRadius: 10, marginBottom: 16,
      background: ok === true ? 'rgba(34,197,94,0.08)' : ok === false ? 'rgba(239,68,68,0.08)' : 'rgba(251,191,36,0.08)',
      border: `1px solid ${ok === true ? 'rgba(34,197,94,0.2)' : ok === false ? 'rgba(239,68,68,0.2)' : 'rgba(251,191,36,0.2)'}`,
      color: ok === true ? '#22C55E' : ok === false ? '#EF4444' : '#FBBF24',
      fontSize: 14, fontWeight: 600,
    }),
    error: { color: '#EF4444', fontSize: 13, marginBottom: 12 } as const,
    warning: { fontSize: 12, color: T.textMuted, marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 } as const,
  };

  return (
    <div style={s.page}>
      <div style={s.title}>🌐 Domínio Personalizado</div>
      <div style={s.subtitle}>Configure um domínio personalizado para sua marca. Seus clientes acessarão sua plataforma pelo seu próprio domínio.</div>

      {/* Input domínio */}
      <div style={s.card}>
        <label style={s.label}>Seu Domínio</label>
        <div style={s.inputRow}>
          <input
            type="text"
            placeholder="meusite.com"
            value={domain}
            onInput={(e: any) => { setDomain(e.target.value); setError(''); }}
            onKeyDown={(e: any) => e.key === 'Enter' && handleSave()}
            style={s.input}
          />
          <button onClick={handleSave} disabled={saving} style={{ ...s.btn, opacity: saving ? 0.6 : 1 }}>
            {saving ? '...' : '💾 Salvar'}
          </button>
        </div>
        {error && <div style={s.error}>{error}</div>}
        {savedDomain && (
          <button onClick={handleRemove} disabled={saving} style={s.btnDanger}>
            🗑️ Remover domínio
          </button>
        )}
      </div>

      {/* Instruções CNAME — só aparece após salvar */}
      {savedDomain && (
        <>
          <div style={s.cnameCard}>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              📋 Configure seu DNS
            </div>
            <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 16 }}>
              Adicione este registro CNAME no painel do seu registrador de domínio:
            </div>

            <div style={s.cnameRow}>
              <span style={s.cnameLabel}>Tipo</span>
              <span style={s.cnameValue}>CNAME</span>
            </div>
            <div style={s.cnameRow}>
              <span style={s.cnameLabel}>Nome</span>
              <span style={s.cnameValue}>{savedDomain}</span>
            </div>
            <div style={{ ...s.cnameRow, borderBottom: 'none' }}>
              <span style={s.cnameLabel}>Valor</span>
              <span style={s.cnameValue}>{CNAME_TARGET}</span>
            </div>

            <div style={{ marginTop: 16 }}>
              <button onClick={handleCopy} style={s.btnOutline}>
                {copied ? '✅ Copiado!' : '📋 Copiar CNAME'}
              </button>
            </div>

            <div style={s.warning}>
              ⚠️ A propagação DNS pode levar até 24 horas. Após configurar, clique em "Verificar DNS".
            </div>
          </div>

          {/* Status de verificação */}
          {verified !== null && (
            <div style={s.status(verified)}>
              {verified ? '✅' : '❌'} {verifyMessage}
            </div>
          )}

          {verified === null && savedDomain && (
            <div style={s.status(null)}>
              ⏳ Aguardando configuração DNS
            </div>
          )}

          {/* Botão verificar */}
          <button onClick={handleVerify} disabled={verifying} style={{ ...s.btn, width: '100%', opacity: verifying ? 0.6 : 1 }}>
            {verifying ? '🔄 Verificando...' : '🔄 Verificar DNS'}
          </button>

          {/* Mensagem de sucesso final */}
          {verified && (
            <div style={{ textAlign: 'center', marginTop: 24, padding: 20, background: 'rgba(34,197,94,0.06)', borderRadius: 12, border: '1px solid rgba(34,197,94,0.15)' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🎉</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#22C55E', marginBottom: 4 }}>
                Seu site está live!
              </div>
              <div style={{ fontSize: 14, color: T.textMuted }}>
                Acesse <a href={`https://${savedDomain}`} target="_blank" style={{ color: T.primary, textDecoration: 'none', fontWeight: 600 }}>{savedDomain}</a>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
